import type { BindingAnchor } from "@bezpart-board/shared";
import type { SceneHost, SceneObject } from "./SceneHost";
import { getLineSettings } from "./lineSession";
import {
  anchorPointOnObject,
  applySegmentPoints,
  isBindableObject,
  resolveSegmentPoints,
} from "./bindings/resolveBindings";
import {
  CONNECTOR_ANCHORS,
  connectorAnchorLabel,
  findBindableAtPoint,
} from "./bindings/bindingSnap";
import {
  applySegmentStyleFields,
  clonePoints,
  computeSegmentBBox,
  createSegmentPreviewSvg,
  segmentLength,
  snapSegmentEnd,
  type PathPoint,
} from "./segmentGeometry";

const MIN_LEN_PX = 6;

type ConnectorHost = Pick<
  SceneHost,
  | "canEdit"
  | "getActiveTool"
  | "getSelection"
  | "findObject"
  | "getObjects"
  | "stagePoint"
  | "addObject"
  | "setSelection"
  | "refreshSelectionChrome"
> & {
  selectionOverlay: HTMLElement;
  onLayoutChange?: () => void;
};

type DragState = {
  sourceId: string;
  sourceAnchor: BindingAnchor;
  start: PathPoint;
};

export type ConnectorAnchorHandlesController = {
  update: () => void;
  destroy: () => void;
};

function positionAnchor(el: HTMLElement, x: number, y: number) {
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
}

export function createConnectorAnchorHandles(
  overlay: HTMLElement,
  host: ConnectorHost
): ConnectorAnchorHandlesController {
  const root = document.createElement("div");
  root.className = "wb-connector-anchors wb-hidden";
  root.setAttribute("aria-hidden", "true");

  const handleEls = new Map<BindingAnchor, HTMLElement>();
  for (const anchor of CONNECTOR_ANCHORS) {
    const el = document.createElement("div");
    el.className = "wb-connector-anchor";
    el.dataset.anchor = anchor;
    el.title = `${connectorAnchorLabel(anchor)} — тяните к другому объекту`;
    root.appendChild(el);
    handleEls.set(anchor, el);
  }
  overlay.appendChild(root);

  const preview = createSegmentPreviewSvg();
  let drag: DragState | null = null;

  function update() {
    // Якоря связей перекрывали боковые ручки ресайза (n/e/s/w) —
    // стрелки по-прежнему рисуются инструментом «Стрелка» с привязкой.
    root.classList.add("wb-hidden");
    return;
  }

  function onDragMove(ev: PointerEvent) {
    if (!drag) return;
    const pt = host.stagePoint(ev.clientX, ev.clientY);
    let end: PathPoint = [pt.x, pt.y];
    if (ev.shiftKey) {
      end = snapSegmentEnd(drag.start, end, true);
    }
    const line = getLineSettings();
    preview.setSegment(
      drag.start,
      end,
      line.stroke,
      line.strokeWidth,
      line.strokeLinecap,
      line.strokeDash ?? "solid"
    );
  }

  function onDragUp(ev: PointerEvent) {
    if (!drag) return;
    const state = drag;
    drag = null;
    document.removeEventListener("pointermove", onDragMove);
    document.removeEventListener("pointerup", onDragUp);
    document.removeEventListener("pointercancel", onDragCancel);
    preview.root.remove();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    let end: PathPoint = [pt.x, pt.y];
    if (ev.shiftKey) {
      end = snapSegmentEnd(state.start, end, true);
    }
    if (segmentLength(state.start, end) < MIN_LEN_PX) {
      update();
      return;
    }

    const exclude = new Set([state.sourceId]);
    const target = findBindableAtPoint(host.getObjects(), end[0], end[1], exclude);
    const line = getLineSettings();
    const style = applySegmentStyleFields({
      stroke: line.stroke,
      strokeWidth: line.strokeWidth,
      strokeLinecap: line.strokeLinecap,
      strokeDash: line.strokeDash,
    });
    style.markerEnd = "triangle";

    const bindings: NonNullable<SceneObject["bindings"]> = {
      start: { objectId: state.sourceId, anchor: state.sourceAnchor },
    };
    if (target) {
      bindings.end = { objectId: target.id, anchor: "auto" };
    }

    const draft: SceneObject = {
      id: crypto.randomUUID(),
      type: "arrow",
      x: 0,
      y: 0,
      w: 1,
      h: 1,
      points: clonePoints([state.start, end]),
      style,
      bindings,
    };

    const resolved = resolveSegmentPoints(draft, host.getObjects());
    if (resolved) {
      applySegmentPoints(draft, resolved[0], resolved[1]);
    } else {
      const bbox = computeSegmentBBox(draft);
      draft.x = bbox.x;
      draft.y = bbox.y;
      draft.w = bbox.w;
      draft.h = bbox.h;
    }

    void host.addObject(draft, `connector-${draft.id}`).then(() => {
      host.setSelection([draft.id]);
      host.refreshSelectionChrome();
    });
  }

  function cancelDrag() {
    if (!drag) return;
    drag = null;
    document.removeEventListener("pointermove", onDragMove);
    document.removeEventListener("pointerup", onDragUp);
    document.removeEventListener("pointercancel", onDragCancel);
    preview.root.remove();
    update();
  }

  function onDragCancel() {
    cancelDrag();
  }

  function onAnchorDown(ev: PointerEvent) {
    if (!host.canEdit) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    const anchor = (ev.currentTarget as HTMLElement).dataset.anchor as BindingAnchor | undefined;
    if (!anchor) return;
    ev.preventDefault();
    ev.stopPropagation();

    const ids = host.getSelection();
    if (ids.length !== 1) return;
    const source = host.findObject(ids[0]!);
    if (!source || !isBindableObject(source)) return;

    const start = anchorPointOnObject(source, anchor);
    drag = { sourceId: source.id, sourceAnchor: anchor, start };
    host.selectionOverlay.appendChild(preview.root);
    preview.setSegment(start, start, "#6246ea", 2, "round", "solid");

    document.addEventListener("pointermove", onDragMove);
    document.addEventListener("pointerup", onDragUp);
    document.addEventListener("pointercancel", onDragCancel);
  }

  for (const el of handleEls.values()) {
    el.addEventListener("pointerdown", onAnchorDown);
  }

  return {
    update,
    destroy() {
      cancelDrag();
      for (const el of handleEls.values()) {
        el.removeEventListener("pointerdown", onAnchorDown);
      }
      root.remove();
    },
  };
}
